import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import {
  Globe,
  Cpu,
  Database,
  Cloud,
  Brain,
  Layers,
  Save,
  Loader2,
  Eye,
  KeyRound,
  Link,
  RefreshCw,
  ChevronDown,
  Pencil,
  Trash2,
  Plus,
  X,
  Check,
} from "lucide-react";
import toast from "react-hot-toast";
import ConfirmationDialog from "../../widgets/ConfirmationModal";
import Input from "../../widgets/Input";
import Dropdownselect from "../../widgets/Dropdown";
import PasswordInput from "../../widgets/PasswordInput";
import FileUpload from "../../widgets/FileUpload";
import { AppConfig } from "../../services/settings.service";
import Loader from "../../widgets/Loader";
import settingsService from "../../services/settings.service";
import { useBusinessSettings } from "../../contexts/BusinessSettingsContext";
import { usePermissions } from "../../hooks/usePermissions";

function BusinessSettings() {
  const [activeTab, setActiveTab] = useState<"site" | "advanced">("site");
  const [isLoading, setIsLoading] = useState(false);
  const {
    settings,
    isLoading: isLoadingSettings,
    updateSettings,
  } = useBusinessSettings();
  const [isConfigExists, setIsConfigExists] = useState(false);
  const [configId, setConfigId] = useState<string | null>(null);

  const [redirectUris, setRedirectUris] = useState<string[]>([]);
  const [isFetchingUris, setIsFetchingUris] = useState(false);
  const [isUriAccordionOpen, setIsUriAccordionOpen] = useState(false);

  // Add
  const [isAdding, setIsAdding] = useState(false);
  const [addValue, setAddValue] = useState("");
  const [isSavingAdd, setIsSavingAdd] = useState(false);

  // Edit
  const [editingUri, setEditingUri] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  // Edit permission gate — viewers can read the config, only editors can submit.
  const { can } = usePermissions();
  const canEdit = can("settings:edit");

  const { control, register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<AppConfig>(
    {},
  );

  const fetchRedirectUris = useCallback(async () => {
    setIsFetchingUris(true);
    try {
      const res = await settingsService.getOnedriveRedirectUris();
      setRedirectUris(res.data.redirect_uris);
    } catch (err: any) {
      const code = err?.response?.data?.error?.error_code;
      if (code !== "missing_onedrive_config") {
        toast.error("Failed to load redirect URIs");
      }
    } finally {
      setIsFetchingUris(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "advanced") {
      fetchRedirectUris();
    }
  }, [activeTab, fetchRedirectUris]);

  const handleAddRedirectUri = async () => {
    const uri = addValue.trim();
    if (!uri) return;
    setIsSavingAdd(true);
    try {
      const res = await settingsService.registerOnedriveRedirectUri(uri);
      toast.success(res.data.message);
      await fetchRedirectUris();
      setAddValue("");
      setIsAdding(false);
    } catch (err: any) {
      const errData = err?.response?.data?.error;
      toast.error(errData?.error_description || "Failed to register redirect URI");
    } finally {
      setIsSavingAdd(false);
    }
  };

  const handleUpdateRedirectUri = async (oldUri: string) => {
    const newUri = editValue.trim();
    if (!newUri || newUri === oldUri) {
      setEditingUri(null);
      return;
    }
    setIsSavingEdit(true);
    try {
      const res = await settingsService.updateOnedriveRedirectUri(oldUri, newUri);
      toast.success(res.data.message);
      await fetchRedirectUris();
      setEditingUri(null);
    } catch (err: any) {
      const errData = err?.response?.data?.error;
      toast.error(errData?.error_description || "Failed to update redirect URI");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteRedirectUri = async () => {
    if (!deleteTarget) return;
    try {
      const res = await settingsService.deleteOnedriveRedirectUri(deleteTarget);
      toast.success(res.data.message);
      await fetchRedirectUris();
    } catch (err: any) {
      const errData = err?.response?.data?.error;
      toast.error(errData?.error_description || "Failed to delete redirect URI");
    } finally {
      setDeleteTarget(null);
    }
  };

  // Provider options
  const aiProviderOptions = [
    { value: "GOOGLE_GENERATIVE_AI", label: "Google Gemini" },
  ];
  const vectorStoreOptions = [{ value: "upstash", label: "Upstash Vector" }];
  const fileStorageOptions = [{ value: "aws", label: "Amazon S3" }];

  // Watch current logo URLs
  const currentExpandedLogoUrl = watch("site.expanded_menu_logo_url") || "";
  const currentCollapsedLogoUrl = watch("site.collapsed_menu_logo_url") || "";

  // Handle file upload
  const handleLogoUpload = async (file: File): Promise<string> => {
    return await settingsService.uploadFile(file);
  };

  // Update form when settings change
  useEffect(() => {
    if (settings) {
      setIsConfigExists(true);
      setConfigId(settings._id || null);
      reset(settings);
    } else {
      setIsConfigExists(false);
      setConfigId(null);
    }
  }, [settings, reset]);

  const onSubmit = async (data: AppConfig) => {
    setIsLoading(true);
    try {
      // Add the ID if it exists for updates
      const settingsData = { ...data };
      if (configId) {
        settingsData._id = configId;
      }

      const success = await updateSettings(settingsData);
      if (success) {
        if (!isConfigExists) {
          setIsConfigExists(true);
          setConfigId(settings?._id || null);
        }
      }
    } catch (error: any) {
      console.error("Error saving settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingSettings) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-2 flex-wrap">
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Business Settings
                </h1>
                <p className="text-xs text-slate-600">
                  Configure your application settings and advanced integrations
                </p>
              </div>
              {canEdit ? (
                <button
                  onClick={handleSubmit(onSubmit)}
                  disabled={isLoading}
                  type="button"
                  className="flex items-center gap-2 pl-5 pr-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {isLoading
                    ? "Saving..."
                    : isConfigExists
                      ? "Update Configuration"
                      : "Save Configuration"}
                </button>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-md">
                  <Eye className="w-3.5 h-3.5" />
                  View-only access
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Read-only banner — explains why nothing's editable for `settings:view`-only users */}
        {!canEdit && (
          <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2.5">
            <Eye className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900">
              <p className="font-semibold mb-0.5">
                You're viewing this page in read-only mode.
              </p>
              <p className="text-amber-800">
                Your role includes{" "}
                <code className="font-mono bg-amber-100 px-1 rounded">
                  settings:view
                </code>{" "}
                but not{" "}
                <code className="font-mono bg-amber-100 px-1 rounded">
                  settings:edit
                </code>
                , so the form is disabled. Ask your admin to grant the edit
                permission if you need to change anything.
              </p>
            </div>
          </div>
        )}

        {/* Tab Navigation + form — fieldset disables every native control inside
            when the user lacks settings:edit. Tab switching buttons stay outside
            so view-only users can still flip between Site and Advanced tabs. */}
        <div
          className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${!canEdit ? "select-none" : ""}`}
        >
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab("site")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "site"
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Site Settings
                </div>
              </button>
              <button
                onClick={() => setActiveTab("advanced")}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === "advanced"
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Cpu className="w-4 h-4" />
                  Advanced Settings
                </div>
              </button>
            </nav>
          </div>

          <fieldset
            disabled={!canEdit}
            className={`p-8 ${!canEdit ? "opacity-90" : ""}`}
          >
            {activeTab === "site" && (
              <div className="space-y-8">
                {/* Site Configuration */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <Globe className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Site Configuration
                    </h2>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Configure your site's basic information and branding
                    elements
                  </p>

                  <div className="grid grid-cols-1 gap-6">
                    <Input
                      name="site.site_name"
                      control={control}
                      label="Site Name"
                      placeholder="Enter your site name"
                      rules={{ 
                        required: "Site name is required",
                        pattern: {
                          value: /^[A-Za-z0-9\s]*$/,
                          message: "Only alphabets, numbers, and spaces are allowed"
                        }
                      }}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Site Description
                      </label>
                      <textarea
                        {...register("site.site_description", {
                          pattern: {
                            value: /^[A-Za-z0-9\s_.,-]*$/,
                            message: "Only alphabets, numbers, spaces, hyphens, underscores, commas and periods are allowed"
                          }
                        })}
                        rows={3}
                        className={`w-full px-4 py-3 border ${errors.site?.site_description ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors resize-none`}
                        placeholder="Brief description of your site"
                      />
                      {errors.site?.site_description && (
                        <p className="mt-1 text-sm text-red-500">{errors.site.site_description.message as string}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FileUpload
                        label="Expanded Menu Logo"
                        accept="image/*"
                        maxSizeMB={5}
                        onFileUpload={handleLogoUpload}
                        currentUrl={currentExpandedLogoUrl}
                        placeholder="PNG, JPG, SVG up to 5MB"
                        onUrlChange={(url) =>
                          setValue("site.expanded_menu_logo_url", url)
                        }
                      />

                      <FileUpload
                        label="Collapsed Menu Logo"
                        accept="image/*"
                        maxSizeMB={5}
                        onFileUpload={handleLogoUpload}
                        currentUrl={currentCollapsedLogoUrl}
                        placeholder="PNG, JPG, SVG up to 5MB"
                        onUrlChange={(url) =>
                          setValue("site.collapsed_menu_logo_url", url)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "advanced" && (
              <div className="space-y-8">
                {/* AI Configuration */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <Brain className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      AI Configuration
                    </h2>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Configure your AI provider settings and model presets for
                    different performance levels
                  </p>

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Dropdownselect
                        name="ai_config.provider"
                        control={control as any}
                        options={aiProviderOptions}
                        label="AI Provider"
                        placeholder="Select AI provider"
                        rules={{ required: "AI provider is required" }}
                      />

                      <PasswordInput
                        name="ai_config.api_key"
                        control={control}
                        label="API Key"
                        placeholder="Enter your API key"
                        rules={{ required: "API key is required" }}
                      />
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Model Presets
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Input
                          name="ai_config.model_presets.high"
                          control={control}
                          label="High Performance"
                          placeholder="gpt-4-turbo"
                          rules={{
                            required: "High performance model is required",
                          }}
                        />

                        <Input
                          name="ai_config.model_presets.medium"
                          control={control}
                          label="Medium Performance"
                          placeholder="gpt-3.5-turbo"
                          rules={{
                            required: "Medium performance model is required",
                          }}
                        />

                        <Input
                          name="ai_config.model_presets.low"
                          control={control}
                          label="Low Performance"
                          placeholder="gpt-3.5-turbo"
                          rules={{
                            required: "Low performance model is required",
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Embedding Context Configuration */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <Layers className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Embedding Context Configuration
                    </h2>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Fine-tune embedding model settings for optimal text
                    processing and retrieval
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Input
                      name="embedding_context_config.embedding_model"
                      control={control}
                      label="Embedding Model"
                      placeholder="text-embedding-004"
                      rules={{ required: "Embedding model is required" }}
                    />

                    <Input
                      name="embedding_context_config.chunk_size"
                      control={control}
                      label="Chunk Size"
                      placeholder="1000"
                      type="number"
                      rules={{ required: "Chunk size is required" }}
                    />

                    <Input
                      name="embedding_context_config.chunk_overlap"
                      control={control}
                      label="Chunk Overlap"
                      placeholder="200"
                      type="number"
                      rules={{ required: "Chunk overlap is required" }}
                    />

                    <Input
                      name="embedding_context_config.top_k_results"
                      control={control}
                      label="Top K Results"
                      placeholder="5"
                      type="number"
                      rules={{ required: "Top K results is required" }}
                    />
                  </div>
                </div>

                {/* Vector Store Configuration */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <Database className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      Vector Store Configuration
                    </h2>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Configure your vector database connection for semantic
                    search and retrieval
                  </p>

                  <div className="space-y-6">
                    <Dropdownselect
                      name="vector_store_config.provider"
                      control={control as any}
                      options={vectorStoreOptions}
                      label="Vector Store Provider"
                      placeholder="Select vector store provider"
                      rules={{ required: "Vector store provider is required" }}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input
                        name="vector_store_config.base_url"
                        control={control}
                        label="Base URL"
                        placeholder="https://your-vector-db.upstash.io"
                        rules={{ required: "Base URL is required" }}
                      />

                      <PasswordInput
                        name="vector_store_config.access_token"
                        control={control}
                        label="Access Token"
                        placeholder="Enter your access token"
                        rules={{ required: "Access token is required" }}
                      />
                    </div>
                  </div>
                </div>

                {/* File Storage Configuration */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <Cloud className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      File Storage Configuration
                    </h2>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Configure cloud storage settings for file uploads and DocIQ
                  </p>

                  <div className="space-y-6">
                    <Dropdownselect
                      name="file_storage_config.provider"
                      control={control as any}
                      options={fileStorageOptions}
                      label="Storage Provider"
                      placeholder="Select storage provider"
                      rules={{ required: "Storage provider is required" }}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Input
                        name="file_storage_config.bucket_name"
                        control={control}
                        label="Bucket Name"
                        placeholder="my-storage-bucket"
                        rules={{ required: "Bucket name is required" }}
                      />

                      <Input
                        name="file_storage_config.region"
                        control={control}
                        label="Region"
                        placeholder="us-east-1"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <PasswordInput
                        name="file_storage_config.access_key_id"
                        control={control}
                        label="Access Key ID"
                        placeholder="Enter access key ID"
                        rules={{ required: "Access key ID is required" }}
                      />

                      <PasswordInput
                        name="file_storage_config.secret_access_key"
                        control={control}
                        label="Secret Access Key"
                        placeholder="Enter secret access key"
                        rules={{ required: "Secret access key is required" }}
                      />
                    </div>

                    <Input
                      name="file_storage_config.endpoint"
                      control={control}
                      label="Custom Endpoint (optional)"
                      placeholder="https://custom-endpoint.com"
                    />
                  </div>
                </div>

                {/* OAuth Credentials */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <KeyRound className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">
                      OAuth Credentials
                    </h2>
                  </div>
                  <p className="text-gray-600 mb-6">
                    Configure OAuth app credentials for Google, Microsoft OneDrive, and Dropbox integrations. Leave blank to use server environment defaults.
                  </p>

                  <div className="space-y-6">
                    {/* Google OAuth */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h3 className="text-base font-semibold text-gray-800 mb-4">
                        Google OAuth
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          name="google_oauth_config.client_id"
                          control={control}
                          label="Client ID"
                          placeholder="Enter Google client ID"
                        />
                        <PasswordInput
                          name="google_oauth_config.client_secret"
                          control={control}
                          label="Client Secret"
                          placeholder="Enter Google client secret"
                        />
                      </div>
                      <div className="mt-4">
                        <Input
                          name="google_oauth_config.redirect_uri"
                          control={control}
                          label="Redirect URI (optional)"
                          placeholder="https://yourdomain.com/auth/google/callback"
                        />
                      </div>
                    </div>

                    {/* Microsoft OneDrive OAuth */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h3 className="text-base font-semibold text-gray-800 mb-4">
                        Microsoft OneDrive OAuth
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          name="onedrive_oauth_config.client_id"
                          control={control}
                          label="Application (Client) ID"
                          placeholder="App registrations → your app → Overview"
                        />
                        <PasswordInput
                          name="onedrive_oauth_config.client_secret"
                          control={control}
                          label="Client Secret Value"
                          placeholder="App registrations → Certificates & secrets"
                        />
                        <Input
                          name="onedrive_oauth_config.tenant_id"
                          control={control}
                          label="Directory (Tenant) ID"
                          placeholder="Azure Active Directory → Overview"
                        />
                        <Input
                          name="onedrive_oauth_config.object_id"
                          control={control}
                          label="Object ID"
                          placeholder="App registrations → your app → Overview"
                        />
                      </div>

                      {/* Redirect URI accordion */}
                      <div className="mt-5 border-t border-gray-100">
                        <button
                          type="button"
                          onClick={() => {
                            const opening = !isUriAccordionOpen;
                            setIsUriAccordionOpen(opening);
                            if (opening) fetchRedirectUris();
                          }}
                          className="w-full flex items-center justify-between pt-4 pb-1 text-left group"
                        >
                          <div className="flex items-center gap-2">
                            <Link className="w-4 h-4 text-gray-500" />
                            <span className="text-sm font-medium text-gray-700">
                              Registered Redirect URIs
                            </span>
                            {redirectUris.length > 0 && (
                              <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded-full font-medium">
                                {redirectUris.length}
                              </span>
                            )}
                          </div>
                          <ChevronDown
                            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isUriAccordionOpen ? "rotate-180" : ""}`}
                          />
                        </button>

                        {isUriAccordionOpen && (
                          <div className="pt-3 pb-1">
                            {isFetchingUris ? (
                              <div className="flex items-center justify-center gap-2 text-xs text-gray-400 py-6">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Loading…
                              </div>
                            ) : (
                              <>
                            {/* Toolbar */}
                            <div className="flex items-center justify-end gap-2 mb-3">
                              <button
                                type="button"
                                onClick={fetchRedirectUris}
                                disabled={isFetchingUris}
                                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                                title="Refresh"
                              >
                                <RefreshCw className={`w-3.5 h-3.5 ${isFetchingUris ? "animate-spin" : ""}`} />
                              </button>
                              {canEdit && !isAdding && (
                                <button
                                  type="button"
                                  onClick={() => { setIsAdding(true); setAddValue(""); }}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                  Add Redirect URI
                                </button>
                              )}
                            </div>

                            {/* List */}
                            <ul className="space-y-1.5">
                                {redirectUris.map((uri) => (
                                  <li
                                    key={uri}
                                    className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md border border-gray-200 group"
                                  >
                                    {editingUri === uri ? (
                                      <>
                                        <input
                                          type="text"
                                          value={editValue}
                                          onChange={(e) => setEditValue(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") handleUpdateRedirectUri(uri);
                                            if (e.key === "Escape") setEditingUri(null);
                                          }}
                                          autoFocus
                                          className="flex-1 text-xs font-mono px-2 py-1 border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => handleUpdateRedirectUri(uri)}
                                          disabled={isSavingEdit}
                                          className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors disabled:opacity-60"
                                          title="Save"
                                        >
                                          {isSavingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => setEditingUri(null)}
                                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                          title="Cancel"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                                        <span className="flex-1 text-xs text-gray-700 font-mono break-all">{uri}</span>
                                        {canEdit && (
                                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                              type="button"
                                              onClick={() => { setEditingUri(uri); setEditValue(uri); }}
                                              className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                              title="Edit"
                                            >
                                              <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => setDeleteTarget(uri)}
                                              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                              title="Delete"
                                            >
                                              <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </li>
                                ))}

                                {redirectUris.length === 0 && !isAdding && (
                                  <p className="text-xs text-gray-400 italic py-1">
                                    No redirect URIs registered yet. Save your Directory (Tenant) ID and Object ID first, then add a URI.
                                  </p>
                                )}

                                {/* Add row */}
                                {isAdding && (
                                  <li className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-md border border-blue-200">
                                    <Link className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                                    <input
                                      type="text"
                                      value={addValue}
                                      onChange={(e) => setAddValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") handleAddRedirectUri();
                                        if (e.key === "Escape") { setIsAdding(false); setAddValue(""); }
                                      }}
                                      placeholder="https://your-server.com/dms-v2-srv/one-drive-file-storage-srv/one-drive-callback"
                                      autoFocus
                                      className="flex-1 text-xs font-mono px-2 py-1 border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                                    />
                                    <button
                                      type="button"
                                      onClick={handleAddRedirectUri}
                                      disabled={isSavingAdd || !addValue.trim()}
                                      className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors disabled:opacity-60"
                                      title="Register"
                                    >
                                      {isSavingAdd ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => { setIsAdding(false); setAddValue(""); }}
                                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                      title="Cancel"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </li>
                                )}
                              </ul>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Dropbox OAuth */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h3 className="text-base font-semibold text-gray-800 mb-4">
                        Dropbox OAuth
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          name="dropbox_oauth_config.client_id"
                          control={control}
                          label="Client ID"
                          placeholder="Enter Dropbox client ID"
                        />
                        <PasswordInput
                          name="dropbox_oauth_config.client_secret"
                          control={control}
                          label="Client Secret"
                          placeholder="Enter Dropbox client secret"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </fieldset>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteRedirectUri}
        iconName="delete"
        title="Delete Redirect URI"
        description={`Are you sure you want to remove this redirect URI from Azure?<br/><br/><code class="text-xs break-all">${deleteTarget ?? ""}</code>`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
}

export default BusinessSettings;
